import { useEffect, useState } from 'react';
import { useQuery, ApolloError } from '@apollo/client';
import {
    EventObject,
    GetFacebookPixelIdDocument,
    GetFacebookPixelIdQuery,
} from '../graphql/graphql';
import metaPixelService from '@/utils/metaPixelService';

interface EventForMeta
    extends Omit<
        EventObject,
        | 'status'
        | 'additionalFees'
        | 'currency'
        | 'title'
        | 'waitlistEnabled'
        | 'requirePurchaserAddress'
    > {}

interface UseEventPixelReturn {
    trackEvent: (eventName: string, params?: Record<string, any>) => void;
    trackEventOnEventPixel: (eventName: string, params?: Record<string, any>) => void;
    trackCustomEvent: (eventName: string, params?: Record<string, any>) => void;
    trackCustomEventOnEventPixel: (eventName: string, params?: Record<string, any>) => void;
    eventPixelId: string | null;
    initialized: boolean;
    loading: boolean;
    error: ApolloError | undefined;
    eventData: EventForMeta | null;
}

const useEventPixel = (eventId?: string): UseEventPixelReturn => {
    const [eventPixelId, setEventPixelId] = useState<string | null>(null);
    const [initialized, setInitialized] = useState<boolean>(false);

    // Fetch event data containing fbpid
    const { data, loading, error } = useQuery<GetFacebookPixelIdQuery>(GetFacebookPixelIdDocument, {
        variables: { eventId },
        skip: !eventId,
    });

    // Initialize the pixel when data loads
    useEffect(() => {
        if (data?.event?.fbpid) {
            const pixelId = data.event.fbpid;
            setEventPixelId(pixelId);

            // Initialize this pixel if it's not already initialized
            const wasInitialized = metaPixelService.initializePixel(pixelId);

            // If this is a newly initialized pixel, track PageView
            if (wasInitialized) {
                metaPixelService.trackEventOnPixel(pixelId, 'PageView');
            }

            setInitialized(true);
        }
    }, [data]);

    // Wrapper methods to make tracking easier
    const trackEvent = (eventName: string, params: Record<string, any> = {}): void => {
        metaPixelService.trackEvent(eventName, params);
    };

    const trackEventOnEventPixel = (eventName: string, params: Record<string, any> = {}): void => {
        if (eventPixelId) {
            metaPixelService.trackEventOnPixel(eventPixelId, eventName, params);
        }
    };

    const trackCustomEvent = (eventName: string, params: Record<string, any> = {}): void => {
        metaPixelService.trackCustomEvent(eventName, params);
    };

    const trackCustomEventOnEventPixel = (
        eventName: string,
        params: Record<string, any> = {}
    ): void => {
        if (eventPixelId) {
            metaPixelService.trackCustomEventOnPixel(eventPixelId, eventName, params);
        }
    };

    return {
        trackEvent,
        trackEventOnEventPixel,
        trackCustomEvent,
        trackCustomEventOnEventPixel,
        eventPixelId,
        initialized,
        loading,
        error,
        eventData: data?.event ? data.event : null,
    };
};

export default useEventPixel;
